import { generateUUID, unique } from '../util';
import { z } from 'zod';
import { MediaNodeSchema } from './media';
import { BingoValidationSchema } from './bingo';
import { SkinRecordSchema } from './skin';

// export const QrSettingsSchema = z.object({
// 	payload: z.string().default(''),
// });
// export type QrSettings = z.infer<typeof QrSettingsSchema>;

export const GPSSettingsSchema = z.object({
	displayName: z.string().optional(),
	lat: z.number().default(0),
	lng: z.number().default(0),
	radius: z.number().int().positive().default(1),
	showCoordinates: z.boolean().default(false),
});
export type GPSSettings = z.infer<typeof GPSSettingsSchema>;

/*************************************
 * GAME ROUND TASK
 *************************************/

export const GameTaskAvailabilityOptions = ['always', 'gps', 'task-completed', 'task-completed-correct' /*, 'qr-code'*/] as const;
export const GameTaskAvailabilitySchema = z.enum(GameTaskAvailabilityOptions);
export type GameTaskAvailability = z.infer<typeof GameTaskAvailabilitySchema>;

export const GameTaskTypeSchema = z.enum([
	'validation-by-game-master',
	'automatic-validation',
	'information',
	'fullscreen-video',
	'augmented-reality',
	'bingo',
]);
export type GameTaskType = z.infer<typeof GameTaskTypeSchema>;

export const GameMasterValidationKindSchema = z.enum(['open', 'photo', 'video']);
export type GameMasterValidationKind = z.infer<typeof GameMasterValidationKindSchema>;

export const GameMasterValidationSchema = z.object({
	kind: GameMasterValidationKindSchema,
});
export type GameMasterValidation = z.infer<typeof GameMasterValidationSchema>;

export const OpenAnswerSchema = z.object({
	id: z.string().uuid().default(generateUUID),
	name: z.string(),
});

export const MultipleChoiceAnswerSchema = z.object({
	id: z.string().uuid().default(generateUUID),
	name: z.string(),
	correct: z.boolean().default(false),
});
export type MultipleChoiceAnswer = z.infer<typeof MultipleChoiceAnswerSchema>;

export const AutomaticValidationKindSchema = z.enum(['open', 'multiple-choice', 'multi-choice']);
export type AutomaticValidationKind = z.infer<typeof AutomaticValidationKindSchema>;

export const AutomaticValidationSchema = z.object({
	kind: AutomaticValidationKindSchema.default('open'),
	openAnswers: z.array(OpenAnswerSchema).optional(),
	openMarginForError: z.number().nonnegative().default(0),
	multipleChoiceAnswers: z.array(MultipleChoiceAnswerSchema).optional(),
});
export type AutomaticValidation = z.infer<typeof AutomaticValidationSchema>;

export const AugmentedRealityModels = z.enum([
	'pagoda',
	'carnavalwagen',
	'karaf',
	'kruidenkraam',
	'moskee',
	'vrijheidsbeeld',
	'ukraine',
	'sitar',
]);

export const AugmentedRealitySchema = z.object({ model: AugmentedRealityModels });
export type AugmentedReality = z.infer<typeof AugmentedRealitySchema>;
export const GameTaskSchema = z.object({
	id: z.string().uuid().default(generateUUID),
	title: z.string().min(1).default('New task'),
	subtitle: z.string().optional(),
	description: z.string().optional(),
	defaultCompletionResponse: z.string().optional(),
	defaultCompletionResponseWrong: z.string().optional(),
	attempts: z.number().min(0).default(1),
	points: z.number().min(0).default(0),
	media: MediaNodeSchema.optional(),
	autoplayMedia: z.boolean().default(false),
	gameMasterInstructions: z.string().optional(),

	availability: GameTaskAvailabilitySchema.default('always'),
	// NOTE: all availability configs may be saved in a task schema so no data is lost when you switch from one availability method to the other
	// when you have a config for a different strategy than is set in availability it simply goes unused

	// used if availability == 'gps'
	availabilityGpsConfig: GPSSettingsSchema.optional(),
	// used if availability == 'qr'
	// availabilityQrConfig: QrSettingsSchema.optional(),
	// used if availability == 'task-completed': id of the task that should be completed to make this one available
	availabilityTaskToComplete: z.string().optional(),

	type: GameTaskTypeSchema.default('validation-by-game-master'),
	typeGameMasterValidationConfig: GameMasterValidationSchema.optional(),
	typeAutomaticValidationConfig: AutomaticValidationSchema.optional(),
	typeAugmentedRealityConfig: AugmentedRealitySchema.optional(),
	typeBingoConfig: BingoValidationSchema.optional(),
});
export type GameTask = z.infer<typeof GameTaskSchema>;

/*************************************
 * GAME ROUND
 *************************************/

export const GameRoundAvailabilitySchema = z.enum([
	'always',
	'game-master',
	'time-minutes',
	'round-completed',
	'gps',
	// 'qr-code',
]);
export type GameRoundAvailability = z.infer<typeof GameRoundAvailabilitySchema>;

export const GameRoundSchema = z.object({
	id: z.string().uuid().default(generateUUID),
	title: z.string().min(1).default('New Round'),
	availability: GameRoundAvailabilitySchema.default('always'),

	// NOTE: all availability configs may be saved in a round schema so no data is lost when you switch from one availability method to the other
	// when you have a config for a different strategy than is set in availability it simply goes unused

	// used if availability == 'round-completed': id of the round that should be completed to make this one available
	availabilityRoundToComplete: z.string().optional(),
	// used if availability == 'gps'
	availabilityGpsConfig: GPSSettingsSchema.optional(),
	// used if availability == 'qr'
	// availabilityQrConfig: QrSettingsSchema.optional(),
	availabilityTimeValue: z.number().int().positive().catch(0),

	tasks: z.array(GameTaskSchema).default([]),
});
export type GameRound = z.infer<typeof GameRoundSchema>;

/*************************************
 * GAME
 *************************************/

export const LanguagesSchema = z.enum(['nl', 'en', 'de', 'fr', 'es', 'it']);
export type Languages = z.infer<typeof LanguagesSchema>;

// TODO: Define template styling options
export const GameTypeSchema = z.enum(['standard', 'double-dealer', 'the-mask', 'quiz']);
export type GameType = z.infer<typeof GameTypeSchema>;

// For defining a variable I want the key to be unique and the value (fallback) to be non empty
export const GameVariableDefinitionSchema = z.object({
	key: z.custom<string>((val) => (typeof val === 'string' ? /^[a-z]+$/i.test(val) : false)).transform((e) => e.toUpperCase()),
	value: z.string().nonempty(),
});
export type GameVariableDefinition = z.infer<typeof GameVariableDefinitionSchema>;

//For defining a variable (value) the key needs to match the validation of the definition but the value can be nullish
export const GameVariableSchema = z.object({
	key: z.custom<string>((val) => (typeof val === 'string' ? /^[a-z]+$/i.test(val) : false)).transform((e) => e.toUpperCase()),
	value: z.string().optional(),
});
export type GameVariable = z.infer<typeof GameVariableSchema>;

export const GameVariableDefinitionArray = z
	.array(GameVariableDefinitionSchema)
	.superRefine(unique([{ key: 'key', message: 'Variable name needs to be unique.' }]))
	.default([]);
export type GameVariableDefinitions = z.infer<typeof GameVariableDefinitionArray>;

export const GameAutomaticNavigationSchema = z.enum(['none', 'per-round', 'per-task']);
export type GameAutomaticNavigation = z.infer<typeof GameAutomaticNavigationSchema>;

export const GameStartBySchema = z.enum(['always-available', 'game-master', 'time']);
export type GameStartBy = z.infer<typeof GameStartBySchema>;

export const GameEndsWhenSchema = z.enum(['never', 'game-master', 'duration']);
export type GameEndsWhen = z.infer<typeof GameEndsWhenSchema>;

export const TeamsSchema = z.enum(['default', 'hotseat']);
export type Teams = z.infer<typeof TeamsSchema>;

export const GameSchema = z.object({
	name: z.string().min(1).default('new game'),
	title: z.string().min(1).default('new game'),
	subtitle: z.string().optional(),
	description: z.string().optional(),
	help: z.string().optional(),
	skin: z.string().uuid().nullable().catch(null),
	language: LanguagesSchema.default('en'),
	type: GameTypeSchema.default('standard'),
	automaticNavigation: GameAutomaticNavigationSchema.default('none'),
	gameStartBy: GameStartBySchema.default('always-available'),
	gameEndsWhen: GameEndsWhenSchema.default('game-master'),
	gameStartsByDate: z.string().datetime().optional(),
	duration: z.number().int().positive().optional(),
	teams: TeamsSchema.default('default'),
	showMap: z.boolean().default(true),
	spectateFunction: z.boolean().default(true),
	globalChat: z.boolean().default(false),
	gameMasterChat: z.boolean().default(false),
	hideScore: z.boolean().default(false),
	rounds: z.array(GameRoundSchema).default([]),

	variables: z
		.array(GameVariableDefinitionSchema)
		.superRefine(unique([{ key: 'key', message: 'Variable name needs to be unique.' }]))
		.default([]),
});
export type Game = z.infer<typeof GameSchema>;

export const GameViewSchema = z.object({
	id: z.string(),
	name: z.string(),
	skin: SkinRecordSchema.nullable(),
	type: z.string(),
	language: z.string(),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
});
export type GameView = z.infer<typeof GameViewSchema>;

export const GameRecordSchema = z.object({
	id: z.string().uuid(),
	game: GameSchema,
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
});
export type GameRecord = z.infer<typeof GameRecordSchema>;

export const GameRecordUpdateSchema = z.object({
	id: z.string().uuid(),
	game: GameSchema,
});
export type GameRecordUpdate = z.infer<typeof GameRecordUpdateSchema>;

export const GameTypeOptions = [
	{ label: 'Standard', value: 'standard' },
	{ label: 'Wie is de rat?', value: 'double-dealer' },
	{ label: 'The Mask', value: 'the-mask' },
	{ label: 'Quiz', value: 'quiz' },
];
